import { CommonModule } from '@angular/common';
import { LocalizationModule } from '@abp/ng.core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { DwApiSelectDirective, DwApiSelectObservable } from '@digiwall/core';
import { AutoSizedDropdownDirective } from '@digiwall/ui-kit';
import { DwActiveLookupRequestDto } from '@digiwall/goliath-core/proxy';
import { WorkOrderDayBookDto } from '@digiwall/goliath-documents/proxy';
import {
  PRODUCTION_FOLDER_DROPDOWN_KEYS,
  PRODUCTION_FOLDER_TEXT_KEYS,
  ProductionFolderFilterOptionsService,
  ProductionFolderPlanningFilterKey,
  ProductionFolderPlanningFilterKeys,
} from '../../../planning-common/filters';
import {
  ApiDropdown,
  BaseBoardFilterComponent,
} from '../../board-filter/base-board-filter.component';
import { ProductionFolderPlanningItemFieldsDto } from './production-folder-planning-item-fields';

/**
 * Planner board filter for the ProductionFolder data source. Supplies this source's filter keys,
 * their labels, field extraction and option-label resolution to the shared
 * {@link BaseBoardFilterComponent}, which owns all of the rendering + state + predicate plumbing.
 *
 * DayBook / InterventionType render as static option lists (their full set is cheaply available from
 * the day-book list / enumeration store). Customer / Article render as lazy `dwApiSelect` lookups —
 * the loader resolves labels natively (no extra fetch) and, when ≥2 presets are configured, constrains
 * the results to those preset ids.
 */
@Component({
  selector: 'document-production-folder-board-filter',
  templateUrl: '../../board-filter/base-board-filter.component.html',
  styleUrl: '../../board-filter/base-board-filter.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    LocalizationModule,
    NgSelectModule,
    AutoSizedDropdownDirective,
    DwApiSelectDirective,
  ],
})
export class ProductionFolderBoardFilterComponent extends BaseBoardFilterComponent {
  private readonly options = inject(ProductionFolderFilterOptionsService);

  protected readonly keys = ProductionFolderPlanningFilterKeys;
  protected readonly dropdownKeys: readonly ProductionFolderPlanningFilterKey[] =
    PRODUCTION_FOLDER_DROPDOWN_KEYS;
  protected readonly textKeys: readonly ProductionFolderPlanningFilterKey[] =
    PRODUCTION_FOLDER_TEXT_KEYS;

  protected readonly labelKey: Record<ProductionFolderPlanningFilterKey, string> = {
    [this.keys.dayBook]: 'Doc::DayBook',
    [this.keys.customer]: '::Customer',
    [this.keys.manufacturer]: '::Manufacturer',
    [this.keys.description]: '::Description',
    [this.keys.displayNumber]: '::Number',
  };

  private readonly dayBooks = signal<WorkOrderDayBookDto[]>([]);

  // Lazy lookups; the loader constrains results to the configured presets.
  private readonly customerSelect: DwApiSelectObservable = dto => {
    this.applyPresetIds(this.keys.customer, dto);
    return this.options.customerSelect(dto as DwActiveLookupRequestDto<string>);
  };

  private readonly manufacturerSelect: DwApiSelectObservable = dto => {
    this.applyPresetIds(this.keys.manufacturer, dto);
    return this.options.manufacturerSelect(dto as DwActiveLookupRequestDto<string>);
  };

  protected override readonly apiDropdowns: Record<string, ApiDropdown> = {
    [this.keys.customer]: { apiSelect: this.customerSelect, bindLabel: 'name' },
    [this.keys.manufacturer]: { apiSelect: this.manufacturerSelect, bindLabel: 'code' },
  };

  constructor() {
    super();
    this.options
      .loadDayBooks()
      .pipe(takeUntilDestroyed())
      .subscribe(dayBooks => this.dayBooks.set(dayBooks));
  }

  // ── Static option-label resolution (DayBook / InterventionType) ─────────────────

  private readonly dayBookLabels = computed(
    () => new Map(this.dayBooks().map(d => [d.id ?? '', d.name?.current ?? d.id ?? ''])),
  );

  protected fieldValue(key: string, displayFields: unknown): string | undefined {
    const fields = (displayFields ?? {}) as ProductionFolderPlanningItemFieldsDto;
    switch (key) {
      case this.keys.dayBook:
        return fields.dayBookId;
      case this.keys.customer:
        return fields.customerId;
      case this.keys.manufacturer:
        return fields.manufacturerId;
      case this.keys.description:
        return fields.description;
      case this.keys.displayNumber:
        return fields.displayNumber;
      default:
        return undefined;
    }
  }

  protected optionLabel(key: string, id: string): string {
    switch (key) {
      case this.keys.dayBook:
        return this.dayBookLabels().get(id) ?? id;
      default:
        return id; // Customer / Manufacturer are api-select (labels resolved by the lookup).
    }
  }
}
